import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { REDIRECT_EVENT, reportRoute } from "../../utils/liveSession";

/* ------------------------------------------------------------------
   Follows an admin's "send this person to a page" (Currently Online's
   redirect button, or the mobile app's admin voice assistant). The live
   session poll (utils/liveSession.js) claims the pending redirect and fires
   REDIRECT_EVENT; this navigates the tab and says who sent them, so a page
   change out of nowhere isn't a mystery. Browser Back returns them.

   Also reports every navigation to the session poll, so Currently Online
   and the assistant see the page someone is on within seconds.
------------------------------------------------------------------ */

const NOTICE_MS = 8000;

// Only pages on this site: a path, or a full URL on this host.
const toInternalPath = (target) => {
  const raw = String(target || "").trim();
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
  try {
    const url = new URL(raw);
    if (url.host === window.location.host) return `${url.pathname}${url.search}${url.hash}`;
  } catch (e) {
    // Not a URL.
  }
  return null;
};

const RemoteRedirect = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    reportRoute();
  }, [pathname]);

  useEffect(() => {
    const onRedirect = (event) => {
      const path = toInternalPath(event.detail?.path);
      if (!path) return;
      setNotice({ by: event.detail?.by || "An admin", key: Date.now() });
      if (`${window.location.pathname}${window.location.search}` !== path) navigate(path);
    };
    window.addEventListener(REDIRECT_EVENT, onRedirect);
    return () => window.removeEventListener(REDIRECT_EVENT, onRedirect);
  }, [navigate]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  if (!notice) return null;

  return (
    <div
      role="status"
      className="app-glass fixed left-1/2 top-20 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-2xl border px-4 py-3 text-sm"
    >
      <span className="app-icon-tile h-8 w-8 shrink-0 rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
        <NorthEastRoundedIcon fontSize="small" />
      </span>
      <span>
        <strong>{notice.by}</strong> sent you to this page.
      </span>
      <button
        type="button"
        className="app-icon-btn"
        aria-label="Dismiss"
        onClick={() => setNotice(null)}
      >
        <CloseRoundedIcon fontSize="small" />
      </button>
    </div>
  );
};

export default RemoteRedirect;
